import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  MapPin,
  Plus,
  Pencil,
  Trash2,
  X,
  Search,
  Loader2,
  CheckCircle2,
  FilePlus2,
  Bell,
  Phone,
  Power,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { MechanicAssignmentCityService } from '../../services/mechanicAssignmentCityService';

const BELL_PRESETS = [1800];

const emptyCity = {
  name: '',
  district: '',
  state: '',
  aliases: '',
  sort_order: 0,
  notes: '',
  is_active: true,
  bell_seconds: 1800,
};

const emptyJob = {
  cityInput: '',
  customerName: '',
  phoneNumber: '',
  vehicleNumber: '',
  serviceType: 'Jump Start',
  serviceTag: '',
  problemDescription: '',
  address: '',
  pincode: '',
  locationLink: '',
};

const field =
  'mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20';

function formatBell(sec) {
  const n = Number(sec) || 0;
  if (n < 60) return `${n}s`;
  const m = Math.floor(n / 60);
  const r = n % 60;
  return r ? `${m}m ${r}s` : `${m} min`;
}

const MechanicAssignmentCities = () => {
  const queryClient = useQueryClient();
  const [tab, setTab] = useState('cities');
  const [cityForm, setCityForm] = useState(emptyCity);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [cityFilter, setCityFilter] = useState('');
  const [jobForm, setJobForm] = useState(emptyJob);
  const [matchedMechanics, setMatchedMechanics] = useState([]);
  const [selectedMechanicId, setSelectedMechanicId] = useState(null);
  const [searching, setSearching] = useState(false);
  const [jobResult, setJobResult] = useState(null);
  const [matchedCityPreview, setMatchedCityPreview] = useState(null);

  const { data: cities = [], isLoading } = useQuery({
    queryKey: ['mechanicAssignmentCities'],
    queryFn: () => MechanicAssignmentCityService.listAll(),
  });

  const activeCities = useMemo(() => cities.filter((c) => c.is_active !== false), [cities]);

  const filteredCities = useMemo(() => {
    const q = cityFilter.trim().toLowerCase();
    if (!q) return cities;
    return cities.filter((c) => {
      const blob = [c.name, c.district, c.state, ...(c.aliases || [])].join(' ').toLowerCase();
      return blob.includes(q);
    });
  }, [cities, cityFilter]);

  const saveCityMutation = useMutation({
    mutationFn: async () => {
      const payload = {
        ...cityForm,
        sort_order: Number(cityForm.sort_order) || 0,
        bell_seconds: Number(cityForm.bell_seconds) || 60,
      };
      if (editingId) return MechanicAssignmentCityService.update(editingId, payload);
      return MechanicAssignmentCityService.create(payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['mechanicAssignmentCities']);
      setCityForm(emptyCity);
      setEditingId(null);
      setShowForm(false);
    },
    onError: (err) => alert(err.message || 'Save failed'),
  });

  const deleteCityMutation = useMutation({
    mutationFn: (id) => MechanicAssignmentCityService.remove(id),
    onSuccess: () => queryClient.invalidateQueries(['mechanicAssignmentCities']),
    onError: (err) => alert(err.message || 'Delete failed'),
  });

  const toggleActiveMutation = useMutation({
    mutationFn: ({ id, is_active }) => MechanicAssignmentCityService.update(id, { is_active }),
    onSuccess: () => queryClient.invalidateQueries(['mechanicAssignmentCities']),
  });

  const openCreate = () => {
    setEditingId(null);
    setCityForm(emptyCity);
    setShowForm(true);
  };

  const handleEditCity = (row) => {
    setEditingId(row.id);
    setCityForm({
      name: row.name || '',
      district: row.district || '',
      state: row.state || '',
      aliases: Array.isArray(row.aliases) ? row.aliases.join(', ') : '',
      sort_order: row.sort_order ?? 0,
      notes: row.notes || '',
      is_active: row.is_active !== false,
      bell_seconds: row.bell_seconds ?? 60,
    });
    setShowForm(true);
    setTab('cities');
  };

  const handleSearchMechanics = async () => {
    setJobResult(null);
    setMatchedMechanics([]);
    setSelectedMechanicId(null);
    const cityInput = jobForm.cityInput.trim();
    if (!cityInput) {
      alert('Enter customer city / district');
      return;
    }
    const matched = MechanicAssignmentCityService.matchCity(cityInput, activeCities);
    setMatchedCityPreview(matched);
    if (!matched) {
      alert(`"${cityInput}" is not an active assignment city. Add it under Cities first.`);
      return;
    }
    setSearching(true);
    try {
      const list = await MechanicAssignmentCityService.searchMechanicsForCity(
        matched,
        jobForm.serviceTag || jobForm.serviceType,
      );
      setMatchedMechanics(list);
      if (list[0]?.id) setSelectedMechanicId(String(list[0].id));
      if (!list.length) alert(`No mechanic found for ${matched.name}.`);
    } catch (e) {
      alert(e.message || 'Mechanic search failed');
    } finally {
      setSearching(false);
    }
  };

  const handleRegisterAndAssign = async () => {
    setJobResult(null);
    if (!jobForm.customerName.trim() || !jobForm.phoneNumber.trim() || !jobForm.cityInput.trim()) {
      alert('Customer name, phone, and city are required');
      return;
    }
    setSearching(true);
    try {
      const res = await MechanicAssignmentCityService.registerComplaintAndAssign({
        ...jobForm,
        cityInput: jobForm.cityInput.trim(),
        customerName: jobForm.customerName.trim(),
        phoneNumber: jobForm.phoneNumber.trim(),
        mechanicId: selectedMechanicId,
      });
      setJobResult(res);
      if (res.mechanics) setMatchedMechanics(res.mechanics);
      if (res.matchedCity) setMatchedCityPreview(res.matchedCity);
      if (res.success) {
        setJobForm((prev) => ({
          ...emptyJob,
          cityInput: prev.cityInput,
          serviceType: prev.serviceType,
        }));
      }
    } catch (e) {
      setJobResult({ success: false, error: e.message || 'Failed' });
    } finally {
      setSearching(false);
    }
  };

  return (
    <div className="w-full space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Auto Mechanic Assignment City</h1>
          <p className="mt-0.5 text-sm text-slate-500">
            Configure city coverage and bell time, then register a complaint and auto-assign a mechanic.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm">
            <span className="text-slate-500">Active</span>{' '}
            <span className="font-bold text-slate-900">{activeCities.length}</span>
          </div>
          <div className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm">
            <span className="text-slate-500">Total</span>{' '}
            <span className="font-bold text-slate-900">{cities.length}</span>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200">
        <div className="flex gap-1">
          {[
            { id: 'cities', label: 'Cities', icon: MapPin },
            { id: 'assign', label: 'Register + Assign', icon: FilePlus2 },
          ].map((item) => {
            const Icon = item.icon;
            const on = tab === item.id;
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => setTab(item.id)}
                className={`inline-flex items-center gap-1.5 border-b-2 px-3 py-2.5 text-sm font-semibold transition ${
                  on
                    ? 'border-primary-600 text-primary-700'
                    : 'border-transparent text-slate-500 hover:text-slate-800'
                }`}
              >
                <Icon size={15} />
                {item.label}
              </button>
            );
          })}
        </div>
        {tab === 'cities' ? (
          <button
            type="button"
            onClick={openCreate}
            className="mb-1 inline-flex items-center gap-1.5 rounded-lg bg-primary-600 px-3 py-2 text-sm font-semibold text-white hover:bg-primary-700"
          >
            <Plus size={15} /> Add city
          </button>
        ) : null}
      </div>

      {tab === 'cities' ? (
        <div className="space-y-4">
          {showForm ? (
            <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="mb-4 flex items-center justify-between">
                <h2 className="text-base font-semibold text-slate-900">
                  {editingId ? 'Edit city' : 'New city / district'}
                </h2>
                <button
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                    setEditingId(null);
                    setCityForm(emptyCity);
                  }}
                  className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100"
                >
                  <X size={18} />
                </button>
              </div>

              <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-4">
                <label className="lg:col-span-2">
                  <span className="text-sm font-medium text-slate-700">City name *</span>
                  <input
                    value={cityForm.name}
                    onChange={(e) => setCityForm({ ...cityForm, name: e.target.value })}
                    className={field}
                    placeholder="New Delhi"
                  />
                </label>
                <label>
                  <span className="text-sm font-medium text-slate-700">District</span>
                  <input
                    value={cityForm.district}
                    onChange={(e) => setCityForm({ ...cityForm, district: e.target.value })}
                    className={field}
                  />
                </label>
                <label>
                  <span className="text-sm font-medium text-slate-700">State</span>
                  <input
                    value={cityForm.state}
                    onChange={(e) => setCityForm({ ...cityForm, state: e.target.value })}
                    className={field}
                  />
                </label>
                <label className="md:col-span-2 lg:col-span-2">
                  <span className="text-sm font-medium text-slate-700">Aliases</span>
                  <input
                    value={cityForm.aliases}
                    onChange={(e) => setCityForm({ ...cityForm, aliases: e.target.value })}
                    className={field}
                    placeholder="Delhi, Delhi NCR"
                  />
                </label>
                <label className="md:col-span-2 lg:col-span-2">
                  <span className="text-sm font-medium text-slate-700">Notes</span>
                  <input
                    value={cityForm.notes}
                    onChange={(e) => setCityForm({ ...cityForm, notes: e.target.value })}
                    className={field}
                  />
                </label>
              </div>

              <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50 p-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-2 text-sm font-semibold text-slate-800">
                    <Bell size={15} className="text-primary-600" />
                    Job bell / rate timer
                    <span className="font-normal text-slate-500">
                      — mechanic app countdown for rate / counter
                    </span>
                  </div>
                  <span className="rounded-md bg-white px-2 py-1 text-xs font-bold text-primary-700 ring-1 ring-primary-100">
                    {formatBell(cityForm.bell_seconds)}
                  </span>
                </div>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {BELL_PRESETS.map((sec) => {
                    const on = Number(cityForm.bell_seconds) === sec;
                    return (
                      <button
                        key={sec}
                        type="button"
                        onClick={() => setCityForm({ ...cityForm, bell_seconds: sec })}
                        className={`rounded-md px-2.5 py-1 text-xs font-semibold ${
                          on
                            ? 'bg-primary-600 text-white'
                            : 'bg-white text-slate-700 ring-1 ring-slate-200 hover:bg-slate-100'
                        }`}
                      >
                        {formatBell(sec)}
                      </button>
                    );
                  })}
                  <input
                    type="number"
                    min={15}
                    max={1800}
                    value={cityForm.bell_seconds}
                    onChange={(e) => setCityForm({ ...cityForm, bell_seconds: e.target.value })}
                    className="w-24 rounded-md border border-slate-200 bg-white px-2 py-1 text-xs font-semibold"
                    title="Custom seconds"
                  />
                </div>
              </div>

              <div className="mt-4 flex flex-wrap items-center gap-3">
                <label className="inline-flex items-center gap-2 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    checked={cityForm.is_active !== false}
                    onChange={(e) => setCityForm({ ...cityForm, is_active: e.target.checked })}
                    className="rounded border-slate-300 text-primary-600 focus:ring-primary-500"
                  />
                  Active for auto assignment
                </label>
                <div className="ml-auto flex gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setShowForm(false);
                      setEditingId(null);
                      setCityForm(emptyCity);
                    }}
                    className="rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => saveCityMutation.mutate()}
                    disabled={saveCityMutation.isPending}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-primary-600 px-3 py-2 text-sm font-semibold text-white hover:bg-primary-700 disabled:opacity-50"
                  >
                    {saveCityMutation.isPending ? (
                      <Loader2 size={15} className="animate-spin" />
                    ) : (
                      <CheckCircle2 size={15} />
                    )}
                    {editingId ? 'Save changes' : 'Save city'}
                  </button>
                </div>
              </div>
            </div>
          ) : null}

          <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 px-4 py-3">
              <p className="text-sm font-semibold text-slate-800">Configured cities</p>
              <div className="relative">
                <Search size={14} className="pointer-events-none absolute left-2.5 top-2.5 text-slate-400" />
                <input
                  value={cityFilter}
                  onChange={(e) => setCityFilter(e.target.value)}
                  placeholder="Search city…"
                  className="w-52 rounded-lg border border-slate-200 py-1.5 pl-8 pr-3 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
                />
              </div>
            </div>

            {isLoading ? (
              <div className="flex justify-center py-14 text-slate-400">
                <Loader2 className="animate-spin" />
              </div>
            ) : filteredCities.length === 0 ? (
              <div className="px-4 py-14 text-center text-sm text-slate-500">
                {cities.length === 0
                  ? 'No cities yet. Click Add city to create one.'
                  : 'No cities match your search.'}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-left text-sm">
                  <thead className="bg-slate-50 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    <tr>
                      <th className="px-4 py-3">City</th>
                      <th className="px-4 py-3">District / State</th>
                      <th className="px-4 py-3">Aliases</th>
                      <th className="px-4 py-3">Bell</th>
                      <th className="px-4 py-3">Status</th>
                      <th className="px-4 py-3 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {filteredCities.map((row) => (
                      <tr key={row.id} className="hover:bg-slate-50/80">
                        <td className="px-4 py-3 font-semibold text-slate-900">{row.name}</td>
                        <td className="px-4 py-3 text-slate-600">
                          {[row.district, row.state].filter(Boolean).join(' · ') || '—'}
                        </td>
                        <td className="max-w-[200px] truncate px-4 py-3 text-slate-500">
                          {(row.aliases || []).join(', ') || '—'}
                        </td>
                        <td className="px-4 py-3">
                          <span className="inline-flex items-center gap-1 rounded-md bg-slate-100 px-2 py-1 text-xs font-semibold text-slate-700">
                            <Bell size={11} />
                            {formatBell(row.bell_seconds ?? 60)}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${
                              row.is_active
                                ? 'bg-emerald-50 text-emerald-700'
                                : 'bg-slate-100 text-slate-500'
                            }`}
                          >
                            {row.is_active ? 'Active' : 'Off'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-1">
                            <button
                              type="button"
                              title={row.is_active ? 'Turn off' : 'Turn on'}
                              onClick={() =>
                                toggleActiveMutation.mutate({
                                  id: row.id,
                                  is_active: !row.is_active,
                                })
                              }
                              className="rounded-lg p-1.5 text-slate-500 hover:bg-white hover:text-slate-800"
                            >
                              <Power size={15} />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleEditCity(row)}
                              className="rounded-lg p-1.5 text-slate-500 hover:bg-white hover:text-primary-700"
                            >
                              <Pencil size={15} />
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                if (window.confirm(`Delete ${row.name}?`)) {
                                  deleteCityMutation.mutate(row.id);
                                }
                              }}
                              className="rounded-lg p-1.5 text-slate-500 hover:bg-red-50 hover:text-red-600"
                            >
                              <Trash2 size={15} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 xl:grid-cols-12">
          <div className="space-y-4 rounded-xl border border-slate-200 bg-white p-5 shadow-sm xl:col-span-7">
            <h2 className="text-base font-semibold text-slate-900">Customer & complaint</h2>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <label className="md:col-span-2">
                <span className="text-sm font-medium text-slate-700">City / district *</span>
                <input
                  list="assignment-cities"
                  value={jobForm.cityInput}
                  onChange={(e) => setJobForm({ ...jobForm, cityInput: e.target.value })}
                  placeholder="New Delhi, Gurugram…"
                  className={field}
                />
                <datalist id="assignment-cities">
                  {activeCities.map((c) => (
                    <option key={c.id} value={c.name} />
                  ))}
                </datalist>
              </label>
              <label>
                <span className="text-sm font-medium text-slate-700">Customer name *</span>
                <input
                  value={jobForm.customerName}
                  onChange={(e) => setJobForm({ ...jobForm, customerName: e.target.value })}
                  className={field}
                />
              </label>
              <label>
                <span className="text-sm font-medium text-slate-700">Phone *</span>
                <div className="relative">
                  <Phone size={14} className="pointer-events-none absolute left-3 top-3.5 text-slate-400" />
                  <input
                    value={jobForm.phoneNumber}
                    onChange={(e) => setJobForm({ ...jobForm, phoneNumber: e.target.value })}
                    className={`${field} pl-8`}
                  />
                </div>
              </label>
              <label>
                <span className="text-sm font-medium text-slate-700">Vehicle</span>
                <input
                  value={jobForm.vehicleNumber}
                  onChange={(e) => setJobForm({ ...jobForm, vehicleNumber: e.target.value })}
                  className={field}
                />
              </label>
              <label>
                <span className="text-sm font-medium text-slate-700">Service type</span>
                <select
                  value={jobForm.serviceType}
                  onChange={(e) => setJobForm({ ...jobForm, serviceType: e.target.value })}
                  className={field}
                >
                  <option value="Jump Start">Jump Start</option>
                  <option value="Towing">Towing</option>
                  <option value="Flat Tyre">Flat Tyre</option>
                  <option value="Minor Repair">Minor Repair</option>
                  <option value="Key Lockout">Key Lockout</option>
                  <option value="Fuel Delivery">Fuel Delivery</option>
                </select>
              </label>
              <label className="md:col-span-2">
                <span className="text-sm font-medium text-slate-700">Problem</span>
                <textarea
                  rows={2}
                  value={jobForm.problemDescription}
                  onChange={(e) => setJobForm({ ...jobForm, problemDescription: e.target.value })}
                  className={field}
                />
              </label>
              <label>
                <span className="text-sm font-medium text-slate-700">Address</span>
                <input
                  value={jobForm.address}
                  onChange={(e) => setJobForm({ ...jobForm, address: e.target.value })}
                  className={field}
                />
              </label>
              <label>
                <span className="text-sm font-medium text-slate-700">Pincode</span>
                <input
                  value={jobForm.pincode}
                  onChange={(e) => setJobForm({ ...jobForm, pincode: e.target.value })}
                  className={field}
                />
              </label>
            </div>

            {matchedCityPreview ? (
              <div className="flex flex-wrap items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
                <MapPin size={14} />
                <span>
                  Matched <strong>{matchedCityPreview.name}</strong>
                </span>
                <span className="rounded bg-white px-2 py-0.5 text-xs font-semibold text-slate-700 ring-1 ring-slate-200">
                  Bell {formatBell(matchedCityPreview.bell_seconds ?? 60)}
                </span>
              </div>
            ) : null}

            <div className="flex flex-wrap gap-2 border-t border-slate-100 pt-4">
              <button
                type="button"
                onClick={handleSearchMechanics}
                disabled={searching}
                className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-800 hover:bg-slate-50 disabled:opacity-50"
              >
                {searching ? <Loader2 size={15} className="animate-spin" /> : <Search size={15} />}
                Search mechanic
              </button>
              <button
                type="button"
                onClick={handleRegisterAndAssign}
                disabled={searching}
                className="inline-flex items-center gap-1.5 rounded-lg bg-primary-600 px-3 py-2 text-sm font-semibold text-white hover:bg-primary-700 disabled:opacity-50"
              >
                {searching ? <Loader2 size={15} className="animate-spin" /> : <FilePlus2 size={15} />}
                Register + assign
              </button>
            </div>

            {jobResult ? (
              <div
                className={`rounded-lg px-3 py-2.5 text-sm ${
                  jobResult.success
                    ? 'border border-emerald-200 bg-emerald-50 text-emerald-900'
                    : 'border border-red-200 bg-red-50 text-red-800'
                }`}
              >
                {jobResult.success ? (
                  <div>
                    <p className="font-semibold">{jobResult.message}</p>
                    {jobResult.requestId ? (
                      <Link
                        to={`/service_advisor/complaints/${jobResult.requestId}`}
                        className="font-medium text-primary-700 underline"
                      >
                        Open complaint #{jobResult.requestId}
                      </Link>
                    ) : null}
                  </div>
                ) : (
                  <p>{jobResult.error}</p>
                )}
              </div>
            ) : null}
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm xl:col-span-5">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-base font-semibold text-slate-900">Matched mechanics</h3>
              <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-600">
                {matchedMechanics.length}
              </span>
            </div>
            {searching ? (
              <div className="flex items-center justify-center gap-2 py-16 text-slate-500">
                <Loader2 className="animate-spin" size={18} /> Searching…
              </div>
            ) : matchedMechanics.length === 0 ? (
              <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50 px-4 py-12 text-center text-sm text-slate-500">
                Search first, then pick a mechanic (or top match is used).
              </div>
            ) : (
              <ul className="max-h-[520px] space-y-2 overflow-y-auto">
                {matchedMechanics.map((m) => {
                  const id = String(m.id);
                  const selected = selectedMechanicId === id;
                  return (
                    <li key={id}>
                      <button
                        type="button"
                        onClick={() => setSelectedMechanicId(id)}
                        className={`w-full rounded-lg border px-3 py-2.5 text-left transition ${
                          selected
                            ? 'border-primary-500 bg-primary-50'
                            : 'border-slate-200 hover:border-slate-300'
                        }`}
                      >
                        <p className="font-semibold text-slate-900">
                          {m.mechanic_name || m.name || 'Mechanic'}
                        </p>
                        <p className="text-sm text-slate-600">
                          {m.number || m.phone || '—'}
                          {m.service_tag ? ` · ${m.service_tag}` : ''}
                        </p>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MechanicAssignmentCities;
